import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import formatPrice from '@honda-canada/js-utilities/lib/formatPrice';
import React, { useContext, useRef, useState } from 'react';
import { Box, Checkbox, Copy, H5, Icon, IconWrapper, IframeVideo, Label, Link, Optional, useMediaQueries, } from '@honda-canada/design-system-react';
import { compiler } from 'markdown-to-jsx';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Context from '../service/Context';
import configThemeStyles from '../ConfigureBapStyles';
import gtmForGenericCtaLinkClick from '../../../core/gtm/gtmForGenericCtaLinkClick';
import gtmProtectionAddonCheckbox from '../../../core/gtm/gtmProtectionAddonCheckbox';
import gtmProtectionSelection from '../../../core/gtm/gtmProtectionSelection';
import themeStyles from './ProtectionCard.styles';
import useBapRegisterUserInteraction from '../../../utils/hooks/bap/useBapRegisterUserInteraction';
import { addProtectionAddOn, removeProtectionAddOn, selectCurrentVehicle, toggleProtection, } from '../../../core/reducers/currentVehicle';
import { getProtectionPrice } from '../utils/utils';
import { primaryCalculatorId, selectPrimaryCalculatorIsPaymentMethodCash, selectPrimaryCalculatorPaymentFrequency, selectTermAndRate, } from '../../../core/reducers/financialCalculators';
import { removeAllTagsFromMarkdown } from '../../../utils/markdown';
import { selectBodyTypesForGtm, selectCurrentVehicleFromVehicles } from '../../../core/reducers/vehicles';
import { useAppSelector } from '../../../core/utils';
import { useLanguage } from '../../../utils/sitecoreContext';
const IconContainer = configThemeStyles.apply(IconWrapper, 'IconContainer');
const LearnMoreCta = configThemeStyles.apply(Link, 'LearnMoreCta');
const PaymentFrequency = configThemeStyles.apply(Copy, 'PaymentFrequency');
const MainContainer = themeStyles.apply(Box, 'MainContainer');
const PriceContainer = themeStyles.apply(Box, 'PriceContainer');
const ProtectionAddon = themeStyles.apply(Copy, 'ProtectionAddon');
const ProtectionTitle = themeStyles.apply(H5, 'ProtectionTitle');
const ProtectionCheckbox = themeStyles.apply(Box, 'ProtectionCheckbox');
const ProtectionDescription = themeStyles.apply(Copy, 'ProtectionDescription');
const ProtectionDescriptionWrapper = themeStyles.apply(Box, 'ProtectionDescriptionWrapper');
const ProtectionReadMore = themeStyles.apply(Box, 'ProtectionReadMore');
const VideoContainer = themeStyles.apply(Box, 'VideoContainer');
const AddToBuildIcon = themeStyles.apply(Icon, 'AddToBuildIcon');
const HEADER_HEIGHT = 90;
const AddOnTitleMap = {
    en: 'titleEn',
    fr: 'titleFr',
};
const ProtectionCard = ({ id, name, gtmName, description, videoUrl, defaultAmount, payment, exclusions, hideSelectedButton, hideVideo, addOn, componentName, preventScrollOnExpand, terms, }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const language = useLanguage();
    const { registerUserInteraction } = useBapRegisterUserInteraction('config');
    const { isDesktop } = useMediaQueries();
    const currentPaymentMethodCash = useAppSelector(selectPrimaryCalculatorIsPaymentMethodCash);
    const containerRef = useRef(null);
    const { setShowModal, setItemsToRemove, setItemToAdd } = useContext(Context);
    const [showDescription, setShowDescription] = useState(false);
    const { protectionKeys, protectionAddOnKeys } = useAppSelector(selectCurrentVehicle) || {};
    const paymentFrequency = useAppSelector(selectPrimaryCalculatorPaymentFrequency);
    const termAndRate = useAppSelector(state => selectTermAndRate(state, primaryCalculatorId));
    const isCardSelected = protectionKeys === null || protectionKeys === void 0 ? void 0 : protectionKeys.includes(id);
    const isAddOnSelected = protectionAddOnKeys === null || protectionAddOnKeys === void 0 ? void 0 : protectionAddOnKeys.includes(id);
    const paymentFrequencyLabel = t(`Shared.Common.${paymentFrequency}OptionLabel`);
    const gtmBodyStyle = useAppSelector(selectBodyTypesForGtm);
    const currentVehicle = useAppSelector(selectCurrentVehicleFromVehicles);
    const { inventoryStatus } = useAppSelector(selectCurrentVehicle);
    const readMoreLabel = showDescription ? t('Shared.Common.readLessLabel') : t('Shared.Common.readMoreLabel');
    const gtmPayload = {
        gtmBodyStyle,
        model: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelGtmName,
        trim: removeAllTagsFromMarkdown(currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.trimGtmName),
    };
    const handleOpen = () => {
        var _a, _b, _c;
        if (!preventScrollOnExpand && typeof window !== 'undefined' && ((_a = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) === null || _a === void 0 ? void 0 : _a.offsetTop) && isDesktop) {
            window.scrollTo(0, ((_c = (_b = containerRef.current) === null || _b === void 0 ? void 0 : _b.offsetTop) !== null && _c !== void 0 ? _c : 0) - HEADER_HEIGHT);
        }
        if (!showDescription) {
            gtmForGenericCtaLinkClick({
                ...gtmPayload,
                ctaType: 'explore',
                eventName: 'bap_cta',
                linkText: readMoreLabel,
                componentType: componentName,
                customizeSection: 'configuration conflict',
                availabilityFlag: inventoryStatus,
            });
        }
        setShowDescription((prop) => !prop);
    };
    const handleSelectProtectionAddOn = () => {
        gtmProtectionAddonCheckbox({
            ...gtmPayload,
            protectionName: gtmName !== null && gtmName !== void 0 ? gtmName : name,
            action: isAddOnSelected ? 'uncheck' : 'check',
        });
        if (!isAddOnSelected) {
            dispatch(addProtectionAddOn(id));
            return;
        }
        dispatch(removeProtectionAddOn(id));
    };
    const handleSelectProtection = () => {
        let newItemsToRemove = [];
        if (!(protectionKeys === null || protectionKeys === void 0 ? void 0 : protectionKeys.includes(id)) && (exclusions === null || exclusions === void 0 ? void 0 : exclusions.some(excId => protectionKeys === null || protectionKeys === void 0 ? void 0 : protectionKeys.includes(excId)))) {
            newItemsToRemove = exclusions === null || exclusions === void 0 ? void 0 : exclusions.filter(excId => protectionKeys === null || protectionKeys === void 0 ? void 0 : protectionKeys.includes(excId));
        }
        if (newItemsToRemove.length) {
            setItemsToRemove(newItemsToRemove);
            setItemToAdd(id);
            setShowModal(true);
        }
        else {
            registerUserInteraction();
            dispatch(toggleProtection(id));
            if (!(protectionKeys === null || protectionKeys === void 0 ? void 0 : protectionKeys.includes(id))) {
                gtmProtectionSelection({
                    ...gtmPayload,
                    protectionSelected: gtmName !== null && gtmName !== void 0 ? gtmName : name,
                    inventoryStatus,
                });
            }
        }
    };
    const protectionPrice = getProtectionPrice(defaultAmount, terms, termAndRate);
    return (_jsxs(MainContainer, { ref: containerRef, "data-testid": "cy-bap-protection-container", tabIndex: 0, children: [_jsx(Optional, { when: !hideSelectedButton, children: _jsx(IconContainer, { as: "button", position: "absolute", cursor: "pointer", border: "none", "data-testid": isCardSelected ? 'cy-protection-selected' : 'cy-protection-unselected', "aria-label": isCardSelected ? 'protection selected' : 'protection unselected', onClick: handleSelectProtection, children: _jsx(AddToBuildIcon, { iconColor: "blue", name: isCardSelected ? 'selected' : 'unselected' }) }) }), _jsx(Optional, { when: addOn, children: _jsx(ProtectionAddon, { size: "small", color: "grey.0", children: t('Pages.Bap.protectionAddonLabel') }) }), _jsx(ProtectionTitle, { size: "pricing", "data-testid": "cy-protection-card-title", children: name }), _jsxs(ProtectionDescriptionWrapper, { opened: showDescription, children: [_jsx(ProtectionDescription, { opened: showDescription, size: "regular", "data-testid": "cy-protection-card-description", children: compiler(description) }), _jsx(Optional, { when: !hideVideo && videoUrl && showDescription, children: _jsx(VideoContainer, { children: _jsx(IframeVideo, { responsive: true, position: "relative", source: videoUrl, "data-testid": "cy-trim-card-walk-around-video-modal" }) }) })] }), _jsx(ProtectionReadMore, { size: "small", onClick: handleOpen, children: _jsx(LearnMoreCta, { as: "button", styling: "tertiary2", "data-testid": "cy-bap-accessory-learn-more", children: readMoreLabel }) }), _jsx(Optional, { when: addOn, children: _jsxs(ProtectionCheckbox, { "data-testid": "cy-protection-addon-checkbox", children: [_jsx(Box, { style: { marginTop: '-4px' }, children: _jsx(Checkbox, { containerSize: "small", ariaLabel: "aria label for checkbox", name: "addOn", id: `${name}-${id}`, checked: isAddOnSelected, disabled: !isCardSelected, onClick: () => handleSelectProtectionAddOn() }) }), _jsx(Label, { htmlFor: `${name}-${id}`, "data-testid": "cy-protection-addon-label", styledProps: {
                                display: 'flex',
                                gap: '8px',
                                fontFamily: 'default',
                                fontSize: '14px',
                                lineHeight: ['22px', '22px', '24px'],
                                alignSelf: 'center',
                            }, children: t('Pages.Bap.addExtraProtectionLabel', {
                                // @ts-ignore
                                extraAddOn: addOn === null || addOn === void 0 ? void 0 : addOn[AddOnTitleMap[language]],
                                addOnPrice: formatPrice(addOn === null || addOn === void 0 ? void 0 : addOn.amount, language, 2),
                            }) })] }) }), _jsxs(PriceContainer, { children: [_jsx(Copy, { size: "pricingSmall", pr: "xs", "data-testid": "cy-bap-accessory-price", children: formatPrice(protectionPrice, language, 2) }), _jsx(Optional, { when: !currentPaymentMethodCash, children: _jsxs(PaymentFrequency, { size: "frequency", "data-testid": "cy-bap-accessory-payment-frequency", children: [formatPrice(payment, language, 2), "/", paymentFrequencyLabel] }) })] })] }));
};
export default ProtectionCard;
